import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, deleteField, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { Building2, LogOut, Search, Send, CheckCircle, AlertCircle, Clock, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './common/PageHeader';

function StaffAffiliation({ user, userData }) {
  const navigate = useNavigate();
  const data = userData || user;
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [pendingApplication, setPendingApplication] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);

  const hasCompany = !!data?.companyId;

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (message) { const t = setTimeout(() => setMessage(null), 5000); return () => clearTimeout(t); } }, [message]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load company info if affiliated
      if (data?.companyId) {
        const compDoc = await getDoc(doc(db, 'users', data.companyId));
        if (compDoc.exists()) setCompanyInfo(compDoc.data());
      }

      // Check pending application
      const appQuery = query(
        collection(db, 'staffApplications'),
        where('staffId', '==', user.uid),
        where('status', '==', 'pending')
      );
      const appSnap = await getDocs(appQuery);
      if (!appSnap.empty) {
        const appData = appSnap.docs[0];
        setPendingApplication({ id: appData.id, ...appData.data() });
      }

      // Check pending invites
      // 2026-10-12 A-2: targetUserId 는 초대 생성 시 쓰는 곳이 없고 rules create hasOnly 에도 없어 항상 0건이었다 →
      //   ReceivedInvites.js 와 같이 email(소문자) 기준으로 조회.
      const myEmail = (user.email || data?.email || '').toLowerCase();
      if (myEmail) {
        const invQuery = query(
          collection(db, 'staffInvites'),
          where('email', '==', myEmail),
          where('status', '==', 'pending')
        );
        const invSnap = await getDocs(invQuery);
        setPendingInvites(invSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        console.warn('받은 초대 조회 생략: 로그인 이메일 없음');
      }

    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Leave current company
  const handleLeave = async () => {
    if (!window.confirm('정말 현재 소속을 해제하시겠습니까?\n해제 후에는 회사 데이터에 접근할 수 없습니다.')) return;
    // 2026-10-12 덴클 앱 1.3.0 W1(P0-2): 종전 users 문서 직접 쓰기(companyId 등 삭제)는 rules 금지 필드라 거부됐다 →
    //   서버 callable leaveCompany(입력 없음 — 항상 호출자 본인, 대표 계정 거부, 회사 직원 목록 정리·대표 알림·감사 기록)로 교체.
    try {
      setProcessing(true);
      const leave = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'leaveCompany');
      await leave({});
      setMessage({ type: 'success', text: '소속이 해제되었습니다. 페이지를 새로고침합니다.' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      // 조용한 실패 금지 — 서버가 준 사유(대표 계정·소속 없음 등)를 그대로 보여 준다
      console.error('소속 해제 실패:', e?.code || '', e?.message || e);
      const serverMsg = (e?.code && e.code !== 'functions/internal' && e?.message) ? e.message : '';
      setMessage({ type: 'error', text: serverMsg || '소속 해제에 실패했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally { setProcessing(false); }
  };

  // Search company by business number or email
  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    try {
      setProcessing(true);
      setSearchResult(null);
      const q = searchEmail.trim().toLowerCase();

      // 🔐 2026-10-03 보안 P0-1: users 이메일 검색은 rules 에서 막혔다 → 이메일은 서버 조회(CF findUserByEmail),
      //   사업자번호 검색은 rules 허용(businessNumber 제약 쿼리)이라 그대로 둔다.
      let foundId = null;
      let foundData = null;
      if (q.includes('@')) {
        try {
          const lookup = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'findUserByEmail');
          const res = await lookup({ email: q, mode: 'exact' });
          const hit = ((res.data && res.data.results) || [])[0];
          if (hit) { foundId = hit.uid; foundData = hit; }
        } catch (lookupErr) {
          console.error('업체 조회 실패:', lookupErr);
          setMessage({ type: 'error', text: ['functions/resource-exhausted', 'functions/invalid-argument'].includes(lookupErr?.code)
            ? (lookupErr.message || '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.')
            : '업체 조회에 실패했습니다. 잠시 후 다시 시도해 주세요.' });
          return;
        }
      } else {
        const snap = await getDocs(query(
          collection(db, 'users'),
          where('businessNumber', '==', q.replace(/-/g, ''))
        ));
        if (!snap.empty) { foundId = snap.docs[0].id; foundData = snap.docs[0].data(); }
      }

      if (!foundData) {
        setMessage({ type: 'warning', text: '해당 업체를 찾을 수 없습니다.' });
        return;
      }

      // Must be a business owner (not staff)
      if (foundData.userType === 'staff' || foundData.userType === 'individual') {
        setMessage({ type: 'error', text: '업체 대표 계정만 검색 가능합니다.' });
        return;
      }

      setSearchResult({ id: foundId, ...foundData });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '검색 실패' });
    } finally { setProcessing(false); }
  };

  // Apply to join company
  const handleApply = async (companyId, companyName) => {
    if (!window.confirm(`${companyName}에 가입 신청하시겠습니까?`)) return;
    try {
      setProcessing(true);
      await addDoc(collection(db, 'staffApplications'), {
        staffId: user.uid,
        staffEmail: data?.email || '',
        staffName: data?.name || data?.displayName || '',
        companyId: companyId,
        companyName: companyName,
        status: 'pending',
        type: 'staffApplication',
        createdAt: serverTimestamp(),
      });

      // Also set pendingCompanyId for backward compat
      await updateDoc(doc(db, 'users', user.uid), {
        pendingStaff: true,
        pendingCompanyId: companyId,
        updatedAt: new Date().toISOString(),
      });

      setMessage({ type: 'success', text: `${companyName}에 가입 신청을 보냈습니다. 승인을 기다려주세요.` });
      setSearchResult(null);
      setSearchEmail('');
      await loadData();
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '가입 신청 실패' });
    } finally { setProcessing(false); }
  };

  // Cancel pending application
  const handleCancelApplication = async () => {
    if (!pendingApplication) return;
    if (!window.confirm('가입 신청을 취소하시겠습니까?')) return;
    try {
      setProcessing(true);
      await updateDoc(doc(db, 'staffApplications', pendingApplication.id), { status: 'cancelled' });
      await updateDoc(doc(db, 'users', user.uid), {
        pendingStaff: deleteField(),
        pendingCompanyId: deleteField(),
        updatedAt: new Date().toISOString(),
      });
      setPendingApplication(null);
      setMessage({ type: 'success', text: '가입 신청이 취소되었습니다.' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '취소 실패' });
    } finally { setProcessing(false); }
  };

  // Accept invite
  const handleAcceptInvite = async (invite) => {
    try {
      setProcessing(true);

      // 2026-08-05 보안수정: 초대 수락은 CF에서 초대 검증 후 원자 처리 (클라 직접 write 폐쇄)
      const acceptFn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'acceptStaffInvite');
      await acceptFn({ inviteId: invite.id });

      setMessage({ type: 'success', text: `${invite.companyName}에 합류했습니다. 페이지를 새로고침합니다.` });
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '초대 수락 실패' });
    } finally { setProcessing(false); }
  };

  // Reject invite
  const handleRejectInvite = async (invite) => {
    try {
      setProcessing(true);
      await updateDoc(doc(db, 'staffInvites', invite.id), { status: 'rejected' });
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
      setMessage({ type: 'success', text: '초대를 거절했습니다.' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '거절 실패' });
    } finally { setProcessing(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>로딩 중...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
      <PageHeader icon={Building2} title="소속 관리" />

      {/* Message */}
      {message && (
        <div style={{ ...S.banner, backgroundColor: message.type === 'success' ? '#f0fdf4' : message.type === 'error' ? '#fef2f2' : '#fffbeb', color: message.type === 'success' ? '#16a34a' : message.type === 'error' ? '#dc2626' : '#92400e' }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Current affiliation */}
      {hasCompany ? (
        <div style={S.card}>
          <h3 style={S.sectionTitle}>현재 소속</h3>
          <div style={S.companyCard}>
            <div style={S.companyIcon}>
              <Building2 size={24} color="#6366f1" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                {companyInfo?.businessName || data?.companyName || data?.businessName || '소속 업체'}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {companyInfo?.businessType === 'dental' ? '치과' : companyInfo?.businessType === 'lab' ? '기공소' : companyInfo?.businessType || ''}
                {companyInfo?.address ? ` · ${companyInfo.address}` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                {companyInfo?.email || ''}
              </div>
            </div>
          </div>
          <button onClick={handleLeave} disabled={processing} style={S.dangerBtn}>
            <LogOut size={16} />
            {processing ? '처리 중...' : '소속 해제'}
          </button>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#94a3b8' }}>
            소속 해제 시 개인 회원으로 전환되며, 회사 데이터에 접근할 수 없습니다.
          </p>
        </div>
      ) : (
        <div style={S.card}>
          <h3 style={S.sectionTitle}>현재 소속</h3>
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>
            <Building2 size={32} color="#d1d5db" />
            <p style={{ margin: '8px 0 0', fontSize: 14 }}>소속된 업체가 없습니다</p>
          </div>
        </div>
      )}

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div style={S.card}>
          <h3 style={S.sectionTitle}>받은 초대 ({pendingInvites.length})</h3>
          {pendingInvites.map(inv => (
            <div key={inv.id} style={S.inviteItem}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{inv.companyName || '업체'}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {inv.createdAt?.toDate?.().toLocaleDateString() || ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handleAcceptInvite(inv)} disabled={processing} style={S.acceptBtn}>수락</button>
                <button onClick={() => handleRejectInvite(inv)} disabled={processing} style={S.rejectBtn}>거절</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending application */}
      {pendingApplication && (
        <div style={S.card}>
          <h3 style={S.sectionTitle}>가입 신청 현황</h3>
          <div style={S.pendingItem}>
            <Clock size={18} color="#f59e0b" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{pendingApplication.companyName || '업체'}</div>
              <div style={{ fontSize: 12, color: '#f59e0b' }}>승인 대기 중</div>
            </div>
            <button onClick={handleCancelApplication} disabled={processing} style={S.rejectBtn}>취소</button>
          </div>
        </div>
      )}

      {/* Search & Apply (only if not affiliated and no pending) */}
      {!hasCompany && !pendingApplication && (
        <div style={S.card}>
          <h3 style={S.sectionTitle}>새 업체 가입 신청</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
            가입하려는 업체의 이메일 또는 사업자번호를 검색하세요.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff' }}>
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="이메일 또는 사업자번호"
                value={searchEmail}
                onChange={e => setSearchEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }}
              />
            </div>
            <button onClick={handleSearch} disabled={processing} style={S.searchBtn}>
              <Search size={16} /> 검색
            </button>
          </div>

          {/* Search result */}
          {searchResult && (
            <div style={{ ...S.inviteItem, marginTop: 12 }}>
              <div style={S.companyIcon}>
                <Building2 size={20} color="#6366f1" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{searchResult.businessName || searchResult.name || '업체'}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {searchResult.businessType === 'dental' ? '치과' : searchResult.businessType === 'lab' ? '기공소' : searchResult.businessType || ''}
                  {searchResult.address ? ` · ${searchResult.address}` : ''}
                </div>
              </div>
              <button onClick={() => handleApply(searchResult.id, searchResult.businessName || searchResult.name || '업체')} disabled={processing} style={S.applyBtn}>
                <Send size={14} /> 신청
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  card: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 },
  sectionTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#0f172a' },
  companyCard: { display: 'flex', alignItems: 'center', gap: 14, padding: 16, backgroundColor: '#f8fafc', borderRadius: 10, marginBottom: 12 },
  companyIcon: { width: 48, height: 48, backgroundColor: '#eef2ff', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  dangerBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%', justifyContent: 'center' },
  inviteItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: 10, marginBottom: 8 },
  pendingItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', backgroundColor: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a' },
  acceptBtn: { padding: '6px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  rejectBtn: { padding: '6px 16px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  searchBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  applyBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 16px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  banner: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600 },
};

export default StaffAffiliation;
